import { useEffect, useRef, useState, type FC } from 'react';
import PratusSelect, {
  OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import { Check, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { WorkAssignment } from 'components/IAP/hooks/workAssignmentHooks';
import {
  computeColumnTotals,
  getResourceValue,
  type Ics215ResourceColumn,
} from './buildIcs215ResourceColumns';
import {
  ICS215_COLUMN_LABELS,
  ICS215_OVERFLOW_COLUMNS,
  ICS215_RHN_ROWS,
  ICS215_TABLE_MIN_WIDTH_PX,
  ICS215_TOTAL_ROWS,
} from './constants';
import { assignmentKey } from './draftUtils';
import {
  ICS215_ICON_BTN,
  ICS215_INPUT,
  ICS215_OUTLINE_BTN,
  ICS215_RHN_INPUT,
  ICS215_TRASH_BTN,
} from './ics215Styles';
import { ics204HtmlToPlainText } from '../AssignmentList/ics204RichText';

export type Ics215WorkGroupOption = {
  id: string;
  name: string;
  hasDraftLeader: boolean;
};

const WorkGroupOptionLabel: FC<{
  name: string;
  hasDraftLeader: boolean;
}> = ({ name, hasDraftLeader }) => (
  <span className="d-flex align-items-center gap-2">
    <span>{name}</span>
    <span
      className={hasDraftLeader ? 'text-success' : 'text-warning'}
      style={{ fontSize: '0.75em' }}
      title={
        hasDraftLeader
          ? 'Leader assigned on draft roster'
          : 'No leader assigned on draft roster'
      }
    >
      {hasDraftLeader ? '● Leader ready' : '○ No draft leader'}
    </span>
  </span>
);

export type Ics215LegacyWorkAssignmentsTableProps = {
  assignments: WorkAssignment[];
  resourceColumns: Ics215ResourceColumn[];
  workGroups: Ics215WorkGroupOption[];
  canEdit: boolean;
  focusAssignmentId: string | null;
  onFocusConsumed: () => void;
  onPatchAssignment: (
    work: WorkAssignment,
    patch: Partial<WorkAssignment>
  ) => void;
  onPatchResource: (
    work: WorkAssignment,
    resourceName: string,
    field: 'required' | 'have' | 'need',
    value: string
  ) => void;
  onDeleteAssignment: (work: WorkAssignment) => void;
  onRenameResourceColumn: (fromName: string, toName: string) => void;
  onDeleteResourceColumn: (resourceName: string) => void;
  onAddResourceColumn: (name: string) => void;
  /** Assignment keys with unsaved edits. Those rows swap their delete button for save and cancel. */
  dirtyRowKeys: Set<string>;
  /** Assignment key currently being written, so its save button can be disabled. */
  savingRowKey: string | null;
  onSaveAssignment: (work: WorkAssignment) => void | Promise<void>;
  onCancelAssignment: (work: WorkAssignment) => void;
};

const CELL =
  'tw:border-b tw:border-neutral-800 tw:px-2 tw:py-2 tw:align-top tw:text-white';
const HEADER_CELL =
  'tw:border-b tw:border-neutral-700 tw:bg-neutral-900 tw:px-2 tw:py-2 tw:text-left tw:text-[10px] tw:font-semibold tw:tracking-wide tw:text-neutral-300';
const RHN_STUB =
  'tw:min-w-[3rem] tw:border-b tw:border-r tw:border-neutral-800 tw:bg-neutral-900 tw:px-1 tw:py-2 tw:text-center tw:text-[9px] tw:font-semibold tw:uppercase tw:text-neutral-300';
const RESOURCE_CELL =
  'tw:min-w-[4.5rem] tw:border-x tw:border-b tw:border-neutral-800 tw:px-1 tw:py-1 tw:text-center tw:align-top';
const OVERFLOW_CELL = 'tw:min-w-[9rem] tw:max-w-[14rem]';

export const Ics215LegacyWorkAssignmentsTable: FC<
  Ics215LegacyWorkAssignmentsTableProps
> = ({
  assignments,
  resourceColumns,
  workGroups,
  canEdit,
  focusAssignmentId,
  onFocusConsumed,
  onPatchAssignment,
  onPatchResource,
  onDeleteAssignment,
  onRenameResourceColumn,
  onDeleteResourceColumn,
  onAddResourceColumn,
  dirtyRowKeys,
  savingRowKey,
  onSaveAssignment,
  onCancelAssignment,
}) => {
  const nameInputRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const [addingResourceColumn, setAddingResourceColumn] = useState(false);
  const [newResourceColumnName, setNewResourceColumnName] = useState('');
  const columnTotals = computeColumnTotals(assignments, resourceColumns);
  const leadingColSpan = 2;
  const addResourceColSpan = canEdit ? 1 : 0;
  const kindsColSpan =
    resourceColumns.length > 0
      ? resourceColumns.length + addResourceColSpan
      : 1;
  const totalColSpan =
    leadingColSpan +
    1 +
    Math.max(kindsColSpan, 1) +
    ICS215_OVERFLOW_COLUMNS.length +
    (canEdit ? 1 : 0);

  useEffect(() => {
    if (!focusAssignmentId) return;
    const input = nameInputRefs.current[focusAssignmentId];
    if (!input) return;
    input.focus();
    input.select();
    onFocusConsumed();
  }, [focusAssignmentId, onFocusConsumed, assignments]);

  const minWidth = Math.max(
    ICS215_TABLE_MIN_WIDTH_PX,
    480 +
      (resourceColumns.length + addResourceColSpan) * 80 +
      ICS215_OVERFLOW_COLUMNS.length * 150
  );

  const confirmAddResource = () => {
    const trimmed = newResourceColumnName.trim();
    if (!trimmed) return;
    onAddResourceColumn(trimmed);
    setNewResourceColumnName('');
    setAddingResourceColumn(false);
  };

  const cancelAddResource = () => {
    setAddingResourceColumn(false);
    setNewResourceColumnName('');
  };

  const addResourceControl = (
    <div className="tw:flex tw:min-w-[8rem] tw:flex-col tw:items-stretch tw:gap-1 tw:normal-case">
      {addingResourceColumn ? (
        <>
          <input
            autoFocus
            value={newResourceColumnName}
            placeholder="Resource kind"
            className={cn(ICS215_RHN_INPUT, 'tw:text-left')}
            onChange={(event) => setNewResourceColumnName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                confirmAddResource();
              }
              if (event.key === 'Escape') {
                cancelAddResource();
              }
            }}
          />
          <div className="tw:flex tw:items-center tw:justify-center tw:gap-1">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={cn(
                ICS215_OUTLINE_BTN,
                'tw:h-6 tw:px-2 tw:text-[10px]'
              )}
              onClick={confirmAddResource}
            >
              Add
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className={cn(ICS215_ICON_BTN, 'tw:h-6 tw:px-2 tw:text-[10px]')}
              onClick={cancelAddResource}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={cn(ICS215_OUTLINE_BTN, 'tw:h-7 tw:w-full tw:text-[10px]')}
          onClick={() => setAddingResourceColumn(true)}
        >
          + Add Resource
        </Button>
      )}
    </div>
  );

  return (
    <div className="tw:min-w-0 tw:w-full tw:max-w-full tw:overflow-hidden tw:rounded-md tw:border tw:border-neutral-700">
      <div
        className="tw:w-0 tw:min-w-full tw:overflow-x-auto tw:overscroll-x-contain tw:touch-pan-x tw:[scrollbar-gutter:stable]"
        tabIndex={0}
        aria-label="Operational planning worksheet — scroll horizontally to view additional columns"
      >
        <table
          className="tw:w-max tw:min-w-full tw:border-separate tw:border-spacing-0 tw:text-xs tw:text-white"
          style={{ minWidth }}
        >
          <thead>
            <tr>
              <th className={cn(HEADER_CELL, 'tw:min-w-[9rem]')}>
                {ICS215_COLUMN_LABELS.workGroup}
              </th>
              <th className={cn(HEADER_CELL, 'tw:min-w-[12rem]')}>
                {ICS215_COLUMN_LABELS.workAssignmentName}
              </th>
              <th
                className={cn(HEADER_CELL, 'tw:min-w-[3rem] tw:text-center')}
              />
              {resourceColumns.length > 0 ? (
                <th
                  colSpan={kindsColSpan}
                  className={cn(
                    HEADER_CELL,
                    'tw:border-x tw:border-neutral-700 tw:text-center'
                  )}
                >
                  {ICS215_COLUMN_LABELS.kindsOfResources}
                </th>
              ) : (
                <th
                  className={cn(
                    HEADER_CELL,
                    'tw:min-w-[10rem] tw:border-x tw:border-neutral-700'
                  )}
                >
                  <div className="tw:flex tw:flex-col tw:items-stretch tw:gap-1.5">
                    <span className="tw:text-center">
                      {ICS215_COLUMN_LABELS.kindsOfResources}
                    </span>
                    {canEdit ? addResourceControl : null}
                  </div>
                </th>
              )}
              {ICS215_OVERFLOW_COLUMNS.map((column) => (
                <th key={column.key} className={cn(HEADER_CELL, OVERFLOW_CELL)}>
                  {column.label}
                </th>
              ))}
              {canEdit ? (
                <th
                  className={cn(HEADER_CELL, 'tw:w-10')}
                  aria-label="Actions"
                />
              ) : null}
            </tr>
            {resourceColumns.length > 0 ? (
              <tr>
                <th className={cn(HEADER_CELL, 'tw:bg-neutral-950')} />
                <th className={cn(HEADER_CELL, 'tw:bg-neutral-950')} />
                <th className={cn(HEADER_CELL, 'tw:bg-neutral-950')} />
                {resourceColumns.map((column) => (
                  <th
                    key={column.id}
                    className={cn(
                      HEADER_CELL,
                      RESOURCE_CELL,
                      'tw:border-b tw:border-neutral-700 tw:bg-neutral-950 tw:normal-case'
                    )}
                  >
                    {canEdit ? (
                      <div className="tw:flex tw:items-center tw:gap-0.5">
                        <input
                          key={column.id}
                          defaultValue={column.label}
                          onBlur={(event) => {
                            const next = event.target.value.trim();
                            if (!next) {
                              event.target.value = column.label;
                              return;
                            }
                            if (next !== column.label) {
                              onRenameResourceColumn(column.label, next);
                            }
                          }}
                          className={cn(ICS215_RHN_INPUT, 'tw:font-semibold')}
                        />
                        {resourceColumns.length > 1 ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Delete ${column.label} column`}
                            className={ICS215_TRASH_BTN}
                            onClick={() => onDeleteResourceColumn(column.label)}
                          >
                            <Trash2 className="tw:h-3.5 tw:w-3.5" />
                          </Button>
                        ) : null}
                      </div>
                    ) : (
                      <span className="tw:block tw:text-center tw:normal-case">
                        {column.label}
                      </span>
                    )}
                  </th>
                ))}
                {canEdit ? (
                  <th
                    className={cn(
                      HEADER_CELL,
                      RESOURCE_CELL,
                      'tw:min-w-[9rem] tw:border-b tw:border-neutral-700 tw:bg-neutral-950'
                    )}
                  >
                    {addResourceControl}
                  </th>
                ) : null}
                {ICS215_OVERFLOW_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className={cn(
                      HEADER_CELL,
                      OVERFLOW_CELL,
                      'tw:bg-neutral-950'
                    )}
                  />
                ))}
                {canEdit ? (
                  <th className={cn(HEADER_CELL, 'tw:bg-neutral-950')} />
                ) : null}
              </tr>
            ) : null}
          </thead>
          <tbody>
            {assignments.length === 0 ? (
              <tr>
                <td
                  colSpan={totalColSpan}
                  className="tw:px-3 tw:py-6 tw:text-center tw:text-neutral-400"
                >
                  No work assignments. Add a work assignment to begin.
                </td>
              </tr>
            ) : (
              assignments.map((work) =>
                ICS215_RHN_ROWS.map((rhnRow, rhnIndex) => {
                  const isFirst = rhnIndex === 0;
                  const isLast = rhnIndex === ICS215_RHN_ROWS.length - 1;
                  const blockBorder = 'tw:border-b tw:border-neutral-600';
                  const rhnBorder = isLast
                    ? blockBorder
                    : 'tw:border-b tw:border-dashed tw:border-neutral-700';
                  const key = assignmentKey(work);
                  const rowKey = `${key}-${rhnRow.field}`;

                  return (
                    <tr key={rowKey} className="tw:align-top">
                      {isFirst ? (
                        <td
                          rowSpan={3}
                          className={cn(CELL, blockBorder, 'tw:min-w-[9rem]')}
                        >
                          {canEdit ? (
                            <PratusSelect
                              options={workGroups.map((g) => ({
                                label: (
                                  <WorkGroupOptionLabel
                                    name={g.name}
                                    hasDraftLeader={g.hasDraftLeader}
                                  />
                                ),
                                value: g.id,
                              }))}
                              value={
                                work.work_group_id
                                  ? {
                                      label: (
                                        <WorkGroupOptionLabel
                                          name={work.work_group_name || ''}
                                          hasDraftLeader={
                                            workGroups.find(
                                              (g) => g.id === work.work_group_id
                                            )?.hasDraftLeader ?? false
                                          }
                                        />
                                      ),
                                      value: work.work_group_id,
                                    }
                                  : null
                              }
                              onChange={(newValue) => {
                                const option = newValue as OptionType | null;
                                const id =
                                  typeof option?.value === 'string'
                                    ? option.value
                                    : '';
                                const group = workGroups.find(
                                  (g) => g.id === id
                                );
                                onPatchAssignment(work, {
                                  work_group_id: id || undefined,
                                  work_group_name: group?.name || '',
                                });
                              }}
                              placeholder="Select work group"
                              isClearable
                            />
                          ) : (
                            <span className="tw:text-sm">
                              {work.work_group_name || '—'}
                            </span>
                          )}
                        </td>
                      ) : null}

                      {isFirst ? (
                        <td
                          rowSpan={3}
                          className={cn(CELL, blockBorder, 'tw:min-w-[12rem]')}
                        >
                          {canEdit ? (
                            <textarea
                              ref={(el) => {
                                nameInputRefs.current[key] = el;
                              }}
                              value={ics204HtmlToPlainText(
                                work.work_assignment
                              )}
                              rows={3}
                              className={cn(
                                ICS215_INPUT,
                                'tw:min-h-16 tw:py-1.5'
                              )}
                              onChange={(event) =>
                                onPatchAssignment(work, {
                                  work_assignment: ics204HtmlToPlainText(
                                    event.target.value
                                  ),
                                })
                              }
                            />
                          ) : (
                            <span className="tw:whitespace-pre-wrap tw:text-sm">
                              {ics204HtmlToPlainText(work.work_assignment) ||
                                '—'}
                            </span>
                          )}
                        </td>
                      ) : null}

                      <td className={cn(RHN_STUB, rhnBorder)}>
                        {rhnRow.label}
                      </td>

                      {resourceColumns.length === 0 ? (
                        <td
                          className={cn(CELL, rhnBorder, 'tw:text-neutral-500')}
                        >
                          —
                        </td>
                      ) : (
                        <>
                          {resourceColumns.map((column) => {
                            const values = getResourceValue(
                              work.resource_list,
                              column.label
                            );
                            const display = values[rhnRow.field];
                            return (
                              <td
                                key={column.id}
                                className={cn(RESOURCE_CELL, rhnBorder)}
                              >
                                {canEdit ? (
                                  <input
                                    value={display}
                                    className={ICS215_RHN_INPUT}
                                    onChange={(event) =>
                                      onPatchResource(
                                        work,
                                        column.label,
                                        rhnRow.field,
                                        event.target.value
                                      )
                                    }
                                  />
                                ) : (
                                  <span className="tw:block tw:px-1 tw:py-1 tw:text-[11px]">
                                    {display || '—'}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                          {canEdit ? (
                            <td
                              className={cn(
                                RESOURCE_CELL,
                                rhnBorder,
                                'tw:min-w-[9rem]'
                              )}
                            />
                          ) : null}
                        </>
                      )}

                      {isFirst
                        ? ICS215_OVERFLOW_COLUMNS.map((column) => (
                            <td
                              key={column.key}
                              rowSpan={3}
                              className={cn(CELL, OVERFLOW_CELL, blockBorder)}
                            >
                              {renderOverflowField(
                                work,
                                column.key,
                                canEdit,
                                onPatchAssignment
                              )}
                            </td>
                          ))
                        : null}

                      {isFirst && canEdit ? (
                        <td
                          rowSpan={3}
                          className={cn(CELL, blockBorder, 'tw:w-24')}
                        >
                          <div className="tw:flex tw:justify-end tw:gap-1">
                            {dirtyRowKeys.has(key) ? (
                              <>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-sm"
                                  aria-label="Cancel editing work assignment"
                                  onClick={() => onCancelAssignment(work)}
                                >
                                  <X className="tw:h-4 tw:w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-sm"
                                  aria-label="Save work assignment"
                                  disabled={savingRowKey === key}
                                  onClick={() => void onSaveAssignment(work)}
                                >
                                  <Check className="tw:h-4 tw:w-4" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Delete work assignment"
                                className={ICS215_TRASH_BTN}
                                onClick={() => onDeleteAssignment(work)}
                              >
                                <Trash2 className="tw:h-4 tw:w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )
            )}
          </tbody>
          {assignments.length > 0 && resourceColumns.length > 0 ? (
            <tfoot>
              {ICS215_TOTAL_ROWS.map((totalRow, totalIndex) => (
                <tr
                  key={totalRow.field}
                  className={cn(
                    'tw:bg-neutral-900',
                    totalIndex < ICS215_TOTAL_ROWS.length - 1
                      ? 'tw:border-b tw:border-dashed tw:border-neutral-700'
                      : ''
                  )}
                >
                  <td
                    colSpan={leadingColSpan + 1}
                    className="tw:border-t tw:border-neutral-600 tw:px-2 tw:py-2 tw:text-[9px] tw:font-semibold tw:text-neutral-300"
                  >
                    {totalRow.label}
                  </td>
                  {resourceColumns.map((column) => {
                    const totals = columnTotals[column.id];
                    const value = totals?.[totalRow.field] ?? 0;
                    return (
                      <td
                        key={column.id}
                        className={cn(
                          RESOURCE_CELL,
                          'tw:border-t tw:border-neutral-600 tw:text-[11px]'
                        )}
                      >
                        {value || '—'}
                      </td>
                    );
                  })}
                  {canEdit ? (
                    <td
                      className={cn(
                        RESOURCE_CELL,
                        'tw:min-w-[9rem] tw:border-t tw:border-neutral-600'
                      )}
                    />
                  ) : null}
                  <td
                    colSpan={ICS215_OVERFLOW_COLUMNS.length + (canEdit ? 1 : 0)}
                    className="tw:border-t tw:border-neutral-600"
                  />
                </tr>
              ))}
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};

function renderOverflowField(
  work: WorkAssignment,
  key: (typeof ICS215_OVERFLOW_COLUMNS)[number]['key'],
  canEdit: boolean,
  onPatchAssignment: (
    work: WorkAssignment,
    patch: Partial<WorkAssignment>
  ) => void
) {
  if (key === 'requested_arrival_time') {
    const value = work.requested_arrival_time
      ? toDatetimeLocalValue(work.requested_arrival_time)
      : '';
    if (!canEdit) {
      return (
        <span className="tw:text-xs">
          {value
            ? new Date(work.requested_arrival_time!).toLocaleString()
            : '—'}
        </span>
      );
    }
    return (
      <input
        type="datetime-local"
        value={value}
        className={cn(ICS215_INPUT, 'tw:h-8')}
        onChange={(event) => {
          const next = event.target.value
            ? new Date(event.target.value)
            : undefined;
          onPatchAssignment(work, {
            requested_arrival_time: next,
          });
        }}
      />
    );
  }

  const textValue = String(work[key] ?? '');
  if (!canEdit) {
    return (
      <span className="tw:whitespace-pre-wrap tw:text-xs">
        {textValue || '—'}
      </span>
    );
  }

  if (key === 'status') {
    return (
      <input
        value={textValue}
        className={cn(ICS215_INPUT, 'tw:h-8')}
        onChange={(event) =>
          onPatchAssignment(work, { status: event.target.value })
        }
      />
    );
  }

  return (
    <textarea
      value={textValue}
      rows={3}
      className={cn(ICS215_INPUT, 'tw:min-h-12 tw:py-1.5')}
      onChange={(event) =>
        onPatchAssignment(work, { [key]: event.target.value })
      }
    />
  );
}

function toDatetimeLocalValue(value: Date | string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
